import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Header from '../../components/Header';
import WebinarFahrplan from './WebinarFahrplan';
import { GefahrBestaetigung } from '../../components/marketingForm';
import ReminderTab from './ReminderTab';
import RoomTab from './RoomTab';
import RerunTab from './RerunTab';
import WebinarForm from './WebinarForm';
import CTALinksModal from './CTALinksModal';
import { fetchData } from '../../helpers/apiUtils';
import { formatGermanTime } from '../../helpers/germanTime';
import WebinarPromoChip, {
  PromoHindernis,
  PromoStatus,
  PromoWarnung,
} from '../../components/WebinarPromoChip';
import { useSearchParams } from 'react-router-dom';
import { hasPublicTitle, publicTitleMissing } from './publicTitle';

interface Webinar {
  webinar_id: string;
  /** INTERNER Arbeitstitel — nur im Dashboard. */
  title: string;
  /** Oeffentlicher Titel (Anmelder sehen nur diesen). */
  display_title?: string | null;
  plx_product: string | null;
  plx_product_name: string | null;
  concept: string | null;
  timezone_id: string;
  start_date: string;
  type: 'automated' | 'live' | 'hybrid' | 'replay';
  is_evergreen: boolean | number;
  evergreen_frequency: string | null;
  registration_page_id: string | null;
  signup_url: string | null;
  order_url: string | null;
  missed_webinar_url: string | null;
  doi_url: string | null;
  mc_tag_id: number | null;
  mc_reminder_list_id: string | null;
  is_recurring?: boolean | number;
  recurrence_days?: number[] | null;
  recurrence_time?: string | null;
  next_occurrence_date?: string | null;
  /** Marketing killswitch. 0 = hidden from every ad/newsletter picker. */
  active: boolean | number;
  /** Zentraler Bewerbungsstatus — serverseitig aus denselben Fragmenten wie die Ad-Picker. */
  promo_status?: PromoStatus;
  promo_label?: string;
  promo_hindernisse?: PromoHindernis[];
  promo_warnungen?: PromoWarnung[];
  promo_lp_signup?: number;
  promo_lp_doi?: number;
  promo_lp_fresh?: number;
  promo_reminder?: number;
  promo_public_title?: number;
  promo_room_video?: number;
}

/** Reiter der Seite. Fahrplan, Erinnerungsmails und Raum gelten fuer das ausgewaehlte Webinar. */
const TAB_LISTE = 0;
const TAB_FAHRPLAN = 1;
const TAB_MAILS = 2;
const TAB_RAUM = 3;
const TAB_WIEDERHOLUNG = 4;

/**
 * Die fuenf Pflichtschritte von Block 1 des Fahrplans aus den 0/1-Spalten der Liste — dieselbe Zuordnung wie
 * `services/webinarUi/fahrplan.ts` (der Termin-Befund zaehlt zu beiden Seiten). Bis Runde 2 zaehlte die Liste die
 * sechs Gate-Spalten roh („2 von 6“) und widersprach damit dem Fahrplan („4 fehlen“).
 */
type ListenWebinar = Record<string, unknown>;
const PFLICHT_SCHRITTE: Array<(w: ListenWebinar) => boolean> = [
  (w) => Number(w.promo_public_title) === 1,
  (w) => Number(w.promo_lp_signup) === 1 && Number(w.promo_lp_fresh) === 1,
  (w) => Number(w.promo_lp_doi) === 1 && Number(w.promo_lp_fresh) === 1,
  (w) => Number(w.promo_reminder) === 1,
  (w) => Number(w.promo_room_video) === 1,
];

/** A still-pending mailing for a webinar — drives the deactivation dialog. */
interface ScheduledMailing {
  id: number;
  campaign_id: number;
  client_code: string;
  esp_type: string;
  scheduled_datetime: string;
  status: string;
}

const WebinarManagement: React.FC = () => {
  // Reiter siehe TAB_*. Bewusst KEIN eigener Router-Eintrag — sonst muesste die Seite in App.tsx,
  // Sidebar.tsx UND groupAuth.ts registriert werden (CLAUDE.md), und der /webinar-management-Pfad
  // traegt die Rechte bereits.
  const [activeTab, setActiveTab] = useState(TAB_LISTE);
  // Das ausgewaehlte Webinar: Fahrplan, Erinnerungsmails und Raum zeigen dieses.
  const [fahrplanWebinarId, setFahrplanWebinarId] = useState<string | null>(null);
  const [raumAnker, setRaumAnker] = useState<'video' | 'angebot' | 'automatisch' | undefined>(
    undefined,
  );
  const [suche, setSuche] = useState('');
  const [alleZeigen, setAlleZeigen] = useState(false);
  // Fuer welches Webinar die Reminder gepflegt werden. Wird aus der Liste heraus gesetzt.
  const [reminderWebinarId, setReminderWebinarId] = useState<string | null>(null);
  // Fuer welches Webinar der eigene Raum (Video, Angebot, Probe) gepflegt wird.
  const [roomWebinarId, setRoomWebinarId] = useState<string | null>(null);
  /** Hinweistext zu offenen Reminder-Slots im Deaktivierungsdialog (Auskunft, keine Aktion). */
  const [pendingReminderNote, setPendingReminderNote] = useState<string | null>(null);
  const [webinars, setWebinars] = useState<Webinar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingWebinar, setEditingWebinar] = useState<Webinar | null>(null);

  // Deep-Link `?webinar=<id>` (Vorab-Warnung an Marketing, seit 08.10.2026): oeffnet seit 22.10.2026 den
  // FAHRPLAN dieses Webinars (vorher das Formular mit der Checkliste „Bewerbbar?“), sobald die Liste geladen
  // ist. `deepLinkHandled` verhindert das Wiederoeffnen nach `loadWebinars()`; der Parameter wird danach
  // entfernt.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkWebinarId = searchParams.get('webinar');
  const deepLinkHandled = useRef<string | null>(null);
  const clearDeepLink = () => {
    if (!deepLinkWebinarId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('webinar');
        return next;
      },
      { replace: true },
    );
  };

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [webinarToDelete, setWebinarToDelete] = useState<Webinar | null>(null);
  const [deleting, setDeleting] = useState(false);

  // CTA Links modal state
  const [ctaLinksModalOpen, setCtaLinksModalOpen] = useState(false);
  const [ctaLinksWebinar, setCtaLinksWebinar] = useState<Webinar | null>(null);

  // Aktiv-Toggle / Deaktivierungs-Dialog state
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [webinarToDeactivate, setWebinarToDeactivate] = useState<Webinar | null>(null);
  const [pendingMailings, setPendingMailings] = useState<ScheduledMailing[]>([]);

  const loadWebinars = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchData<Webinar[]>('/api/webinar-management/webinars');
      if (response.success && response.data) {
        setWebinars(response.data);
      } else {
        setError(response.error || 'Failed to load webinars');
      }
    } catch (err) {
      setError('Failed to load webinars');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWebinars();
  }, []);

  useEffect(() => {
    if (!deepLinkWebinarId) {
      // Parameter weg (Formular geschlossen) → derselbe Link darf spaeter wieder oeffnen.
      deepLinkHandled.current = null;
      return;
    }
    if (loading || error || deepLinkHandled.current === deepLinkWebinarId) return;
    deepLinkHandled.current = deepLinkWebinarId;
    const target = webinars.find((w) => w.webinar_id === deepLinkWebinarId);
    if (target) {
      setFahrplanWebinarId(target.webinar_id);
      setReminderWebinarId(target.webinar_id);
      setRoomWebinarId(target.webinar_id);
      setActiveTab(TAB_FAHRPLAN);
    } else {
      setError(`Webinar ${deepLinkWebinarId} nicht gefunden`);
    }
    clearDeepLink();
    // setSearchParams (in clearDeepLink) ist NICHT referenzstabil und gehoert nicht in die Deps —
    // sonst laeuft der Effekt bei jeder URL-Aenderung erneut (reference-react-router-setsearchparams).
  }, [deepLinkWebinarId, loading, error, webinars]);

  const handleAddNew = () => {
    setEditingWebinar(null);
    setShowForm(true);
  };

  const handleEdit = (webinar: Webinar) => {
    setEditingWebinar(webinar);
    setShowForm(true);
  };

  const handleDeleteClick = (webinar: Webinar) => {
    setWebinarToDelete(webinar);
    setDeleteDialogOpen(true);
  };

  const handleCtaLinksClick = (webinar: Webinar) => {
    setCtaLinksWebinar(webinar);
    setCtaLinksModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!webinarToDelete) return;

    setDeleting(true);
    try {
      const response = await fetchData(
        `/api/webinar-management/webinars/${webinarToDelete.webinar_id}`,
        {
          method: 'DELETE',
        },
      );

      if (response.success) {
        setSuccess('Webinar erfolgreich gelöscht');
        loadWebinars();
      } else {
        setError(response.error || 'Löschen fehlgeschlagen');
      }
    } catch (err) {
      setError('Löschen fehlgeschlagen');
      console.error(err);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setWebinarToDelete(null);
    }
  };

  /** PUT the killswitch. Returns true on success. */
  const setWebinarActive = async (webinar: Webinar, active: boolean): Promise<boolean> => {
    // Only `active` is sent: every field guard in the PUT handler is `!== undefined`-gated, so a
    // one-key body skips the URL-reachability probes and the signup-collision re-checks.
    const response = await fetchData(`/api/webinar-management/webinars/${webinar.webinar_id}`, {
      method: 'PUT',
      body: JSON.stringify({ active }),
    });
    if (!response.success) {
      setError(response.error || 'Aktiv-Status konnte nicht geändert werden');
      return false;
    }
    return true;
  };

  const handleToggleActive = async (webinar: Webinar) => {
    const nextActive = !webinar.active;
    setTogglingId(webinar.webinar_id);
    try {
      // Activating is always safe — no confirmation needed.
      if (nextActive) {
        if (await setWebinarActive(webinar, true)) {
          setSuccess(`„${webinar.display_title || webinar.title}“ ist wieder aktiv`);
          loadWebinars();
        }
        return;
      }

      // Deactivating: ask first if mailings are still queued. `active=0` only hides the webinar
      // from the ad pickers — it does NOT stop mailings, so the operator has to decide.
      const response = await fetchData<ScheduledMailing[]>(
        `/api/webinar-management/webinars/${webinar.webinar_id}/scheduled-mailings`,
      );
      if (!response.success) {
        setError(response.error || 'Geplante Mailings konnten nicht geprüft werden');
        return;
      }

      const mailings = response.data || [];
      // Reminder-Slots sind eine AUSKUNFT, keine Entscheidung: der Dispatch gatet auf
      // adserver_ss_webinars.active = 1, die Deaktivierung stoppt sie also von selbst. Ohne
      // diesen Hinweis wundert sich der Operator spaeter, warum die Erinnerungen ausblieben.
      const reminderNote = (response as { reminder_note?: string }).reminder_note;

      if (mailings.length === 0) {
        if (await setWebinarActive(webinar, false)) {
          setSuccess(
            `"${webinar.title}" deaktiviert — aus allen Ads & Newslettern entfernt` +
              (reminderNote ? ` · ${reminderNote}` : ''),
          );
          loadWebinars();
        }
        return;
      }
      setPendingReminderNote(reminderNote || null);

      setPendingMailings(mailings);
      setWebinarToDeactivate(webinar);
      setDeactivateDialogOpen(true);
    } catch (err) {
      setError('Aktiv-Status konnte nicht geändert werden');
      console.error(err);
    } finally {
      setTogglingId(null);
    }
  };

  const closeDeactivateDialog = () => {
    setDeactivateDialogOpen(false);
    setWebinarToDeactivate(null);
    setPendingMailings([]);
    setPendingReminderNote(null);
  };

  /**
   * Confirm deactivation. `cancelMailings` cancels every still-pending mailing via the existing
   * webinar-mailings endpoint (cancels in the ESP + removes the schedule, skips already-sent).
   *
   * Deactivate first, then cancel: deactivating is the reversible half and the primary intent, so
   * if cancelling partially fails the ads are already off and the operator gets a warning telling
   * them to clean up the rest by hand.
   */
  const handleDeactivateConfirm = async (cancelMailings: boolean) => {
    if (!webinarToDeactivate) return;
    const webinar = webinarToDeactivate;
    const campaignIds = Array.from(new Set(pendingMailings.map((m) => m.campaign_id)));

    setTogglingId(webinar.webinar_id);
    closeDeactivateDialog();
    try {
      if (!(await setWebinarActive(webinar, false))) return;

      if (!cancelMailings) {
        setSuccess(`"${webinar.title}" deaktiviert — geplante Mailings gehen weiter raus`);
        loadWebinars();
        return;
      }

      let cancelled = 0;
      const failedCampaigns: number[] = [];
      for (const campaignId of campaignIds) {
        const res = await fetchData<unknown>(
          `/api/webinar-mailings/campaigns/${campaignId}/all-schedules`,
          { method: 'DELETE' },
        );
        const body = res as { success?: boolean; cancelled?: number; warning?: string };
        if (body?.success) {
          cancelled += body.cancelled || 0;
          if (body.warning) setWarning(body.warning);
        } else {
          failedCampaigns.push(campaignId);
        }
      }

      if (failedCampaigns.length > 0) {
        setWarning(
          `"${webinar.title}" ist deaktiviert, aber die Mailings von Kampagne ` +
            `${failedCampaigns.join(', ')} konnten nicht abgebrochen werden — bitte in ` +
            `Webinar-Mailings manuell prüfen.`,
        );
      } else {
        setSuccess(`"${webinar.title}" deaktiviert, ${cancelled} Mailing(s) abgebrochen`);
      }
      loadWebinars();
    } catch (err) {
      setError('Deaktivieren fehlgeschlagen');
      console.error(err);
    } finally {
      setTogglingId(null);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingWebinar(null);
    clearDeepLink();
  };

  const handleFormSaved = (saveWarning?: string, createdWebinarId?: string) => {
    const bearbeitet = editingWebinar?.webinar_id ?? null;
    setShowForm(false);
    setEditingWebinar(null);
    clearDeepLink();
    setSuccess(
      bearbeitet
        ? 'Grunddaten gespeichert'
        : `Webinar angelegt${createdWebinarId ? ` (ID ${createdWebinarId})` : ''} — der Fahrplan zeigt die nächsten Schritte.`,
    );
    // Nach dem Speichern zurueck in den Fahrplan (Bearbeiten) bzw. in den Fahrplan des neuen Webinars.
    const ziel = createdWebinarId ?? bearbeitet;
    if (ziel) {
      setFahrplanWebinarId(ziel);
      setReminderWebinarId(ziel);
      setRoomWebinarId(ziel);
      setActiveTab(TAB_FAHRPLAN);
    }
    // The Landingpage sentence belongs to THIS producer only (LP-staleness on save). It used to be
    // hard-coded into the shared warning banner, which made every other warning — e.g. a failed
    // mailing cancellation — end in a false claim about landing pages. Own it here; the banner
    // renders `warning` verbatim.
    setWarning(
      saveWarning
        ? `${saveWarning} Mailing-Versand wird blockiert, bis die Landingpage aktualisiert ist.`
        : null,
    );
    loadWebinars();
  };

  // Clear messages after 5 seconds (warning stays longer — it's actionable:
  // marketing needs time to notice "LP noch nicht aktualisiert" and go fix it)
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  useEffect(() => {
    if (warning) {
      const timer = setTimeout(() => setWarning(null), 15000);
      return () => clearTimeout(timer);
    }
  }, [warning]);

  const ausgewaehlt = fahrplanWebinarId
    ? (webinars.find((w) => w.webinar_id === fahrplanWebinarId) ?? null)
    : null;

  const waehleWebinar = (id: string, tab: number = TAB_FAHRPLAN) => {
    setFahrplanWebinarId(id);
    setReminderWebinarId(id);
    setRoomWebinarId(id);
    setActiveTab(tab);
  };

  if (showForm) {
    return (
      <Box m="20px">
        <Header
          title="Webinar Management"
          subtitle={editingWebinar ? 'Grunddaten bearbeiten' : 'Neues Webinar anlegen'}
        />
        <WebinarForm
          webinar={editingWebinar}
          onSaved={handleFormSaved}
          onCancel={handleFormClose}
        />
      </Box>
    );
  }

  const heute = new Date();
  const grenze = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() - 14);
  const grenzeIso = `${grenze.getFullYear()}-${String(grenze.getMonth() + 1).padStart(2, '0')}-${String(grenze.getDate()).padStart(2, '0')}`;
  const suchtext = suche.trim().toLowerCase();
  const sichtbar = webinars.filter((w) => {
    if (suchtext) {
      const heu =
        `${w.display_title ?? ''} ${w.title} ${w.webinar_id} ${w.concept ?? ''}`.toLowerCase();
      return heu.includes(suchtext);
    }
    if (alleZeigen) return true;
    const termin = String(w.next_occurrence_date || w.start_date || '').slice(0, 10);
    // Evergreen/Serie haben keinen festen Termin — im Standardfilter nur, solange sie aktiv sind.
    return ((!!w.is_evergreen || !!w.is_recurring) && !!w.active) || termin >= grenzeIso;
  });

  return (
    <Box m="20px">
      <Header
        title="Webinar Management"
        subtitle="Webinare anlegen und Schritt für Schritt fertig machen"
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} variant="scrollable">
          <Tab label="Webinare" />
          <Tab label="Fahrplan" disabled={!fahrplanWebinarId} />
          <Tab label="Erinnerungsmails" />
          <Tab label="Raum" />
          {/* Global, nicht je Webinar: Wiederholung des letzten Webinars im eigenen Raum. */}
          <Tab label="Wiederholung" />
        </Tabs>
      </Box>

      {ausgewaehlt && activeTab >= TAB_FAHRPLAN && activeTab <= TAB_RAUM && (
        <Paper
          variant="outlined"
          sx={{
            px: 2,
            py: 1,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            bgcolor: '#f8fafc',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Webinar:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {hasPublicTitle(ausgewaehlt.display_title)
              ? ausgewaehlt.display_title
              : ausgewaehlt.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatGermanTime(
              ausgewaehlt.next_occurrence_date || ausgewaehlt.start_date,
              'DD.MM.YYYY HH:mm',
            )}{' '}
            Uhr · <span style={{ fontFamily: 'monospace' }}>{ausgewaehlt.webinar_id}</span>
          </Typography>
          <Box sx={{ flex: 1 }} />
          {activeTab !== TAB_FAHRPLAN && (
            <Button size="small" onClick={() => setActiveTab(TAB_FAHRPLAN)}>
              Zum Fahrplan
            </Button>
          )}
          <Button size="small" onClick={() => setActiveTab(TAB_LISTE)}>
            Anderes Webinar wählen
          </Button>
        </Paper>
      )}

      {activeTab === TAB_FAHRPLAN && fahrplanWebinarId && (
        <WebinarFahrplan
          key={fahrplanWebinarId}
          webinarId={fahrplanWebinarId}
          aktiv={ausgewaehlt ? !!ausgewaehlt.active : true}
          umschaltenLaeuft={togglingId === fahrplanWebinarId}
          onBearbeiten={() => ausgewaehlt && handleEdit(ausgewaehlt)}
          onOeffneReminder={() => setActiveTab(TAB_MAILS)}
          onOeffneRaum={(anker) => {
            setRaumAnker(anker);
            setActiveTab(TAB_RAUM);
          }}
          onAktivUmschalten={() => ausgewaehlt && handleToggleActive(ausgewaehlt)}
          onLoeschen={() => ausgewaehlt && handleDeleteClick(ausgewaehlt)}
          onCtaLinks={() => ausgewaehlt && handleCtaLinksClick(ausgewaehlt)}
        />
      )}
      {activeTab === TAB_MAILS && <ReminderTab webinarId={reminderWebinarId} />}
      {activeTab === TAB_RAUM && <RoomTab webinarId={roomWebinarId} anker={raumAnker} />}
      {activeTab === TAB_WIEDERHOLUNG && <RerunTab />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      )}

      {activeTab === TAB_LISTE && (
        <>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
            >
              Neues Webinar
            </Button>
            <TextField
              size="small"
              placeholder="Suchen: Titel, Kürzel oder ID"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              sx={{ minWidth: 280, bgcolor: '#ffffff' }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={alleZeigen}
                  onChange={(e) => setAlleZeigen(e.target.checked)}
                />
              }
              label={alleZeigen ? 'Alle Webinare' : 'Nur kommende + letzte 14 Tage'}
            />
            <Typography variant="caption" color="text.secondary">
              Zeile anklicken öffnet den Fahrplan des Webinars.
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    {(
                      [
                        ['Webinar', '44%'],
                        ['Termin', '13%'],
                        ['Produkt', '15%'],
                        ['Stand', '18%'],
                        ['', '10%'],
                      ] as const
                    ).map(([h, breite]) => (
                      <TableCell
                        key={h || 'aktion'}
                        sx={{
                          backgroundColor: '#e0e0e0 !important',
                          fontWeight: 'bold',
                          width: breite,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sichtbar.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary">
                          {suchtext
                            ? 'Kein Webinar passt zur Suche.'
                            : 'Keine kommenden Webinare — Schalter „Alle Webinare“ zeigt auch ältere.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sichtbar.map((webinar) => {
                      const erledigt = PFLICHT_SCHRITTE.filter((f) =>
                        f(webinar as unknown as ListenWebinar),
                      ).length;
                      return (
                        <TableRow
                          key={webinar.webinar_id}
                          hover
                          onClick={() => waehleWebinar(webinar.webinar_id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            {/* Oeffentlich gross, intern klein und grau — beide Felder sahen bis 2026-10-02 gleich
                                aus, und der Arbeitstitel landete in den Mails. */}
                            {hasPublicTitle(webinar.display_title) ? (
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ maxWidth: '100%' }}
                                noWrap
                                title={String(webinar.display_title)}
                              >
                                {webinar.display_title}
                              </Typography>
                            ) : publicTitleMissing(webinar) ? (
                              <Typography variant="body2" fontWeight={600} color="error">
                                Öffentlicher Titel fehlt
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                (kein öffentlicher Titel)
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              sx={{ display: 'block', maxWidth: '100%' }}
                            >
                              intern: {webinar.title} · {webinar.concept ?? '–'} ·{' '}
                              <span style={{ fontFamily: 'monospace' }}>{webinar.webinar_id}</span>
                              {webinar.is_evergreen
                                ? ' · Evergreen'
                                : webinar.is_recurring
                                  ? ' · wiederkehrend'
                                  : ''}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatGermanTime(
                              webinar.next_occurrence_date || webinar.start_date,
                              'DD.MM.YYYY HH:mm',
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" title={webinar.plx_product || undefined}>
                              {webinar.plx_product_name || webinar.plx_product || '–'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <WebinarPromoChip
                                status={webinar.promo_status}
                                label={webinar.promo_label}
                                hindernisse={webinar.promo_hindernisse}
                              />
                              {webinar.promo_status === 'unvollstaendig' && (
                                <Typography variant="caption" color="text.secondary">
                                  {erledigt} von {PFLICHT_SCHRITTE.length} Pflichtschritten (Block
                                  1)
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                waehleWebinar(webinar.webinar_id);
                              }}
                            >
                              Öffnen
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <GefahrBestaetigung
        open={deleteDialogOpen}
        titel="Webinar löschen?"
        wirkung={
          <>
            „
            {webinarToDelete
              ? hasPublicTitle(webinarToDelete.display_title)
                ? webinarToDelete.display_title
                : webinarToDelete.title
              : ''}
            “ wird endgültig gelöscht — samt Raum-Einstellungen. Das lässt sich nicht rückgängig
            machen. Hängen noch Heat-Up- oder Erinnerungsmails daran, verweigert das System das
            Löschen.
          </>
        }
        knopfText="Endgültig löschen"
        bestaetigungsWort={webinarToDelete?.webinar_id}
        laeuft={deleting}
        onBestaetigen={handleDeleteConfirm}
        onAbbrechen={() => {
          setDeleteDialogOpen(false);
          setWebinarToDelete(null);
        }}
      />

      {/* Deactivation Dialog — only shown when mailings are still queued */}
      <Dialog open={deactivateDialogOpen} onClose={closeDeactivateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Webinar deaktivieren?</DialogTitle>
        <DialogContent>
          {pendingReminderNote && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {pendingReminderNote}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            &quot;{webinarToDeactivate?.title}&quot; hat noch {pendingMailings.length} geplante
            Mailing(s):
          </DialogContentText>
          <Box sx={{ mb: 2, maxHeight: 220, overflowY: 'auto' }}>
            <Table size="small">
              <TableBody>
                {pendingMailings.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell sx={{ py: 0.5 }}>
                      <Chip label={m.client_code.toUpperCase()} size="small" color="primary" />
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Typography variant="body2" color="textSecondary">
                        {m.esp_type}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Typography variant="body2">
                        {formatGermanTime(m.scheduled_datetime, 'DD.MM.YYYY HH:mm')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <DialogContentText>
            Deaktivieren entfernt das Webinar aus allen Ads &amp; Newslettern. Die geplanten
            Mailings laufen davon unabhängig weiter — bitte entscheiden, was damit passieren soll.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeactivateDialog}>Abbrechen</Button>
          <Button onClick={() => handleDeactivateConfirm(false)} color="warning">
            Nur deaktivieren
          </Button>
          <Button onClick={() => handleDeactivateConfirm(true)} color="error">
            Deaktivieren + Mailings abbrechen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Altbestand, nur Admin (Knopf im Fahrplan unter NurAdmin) */}
      <CTALinksModal
        open={ctaLinksModalOpen}
        onClose={() => {
          setCtaLinksModalOpen(false);
          setCtaLinksWebinar(null);
        }}
        webinar={ctaLinksWebinar}
        onSaved={loadWebinars}
      />
    </Box>
  );
};

export default WebinarManagement;
